import { useCallback, useMemo } from 'react';
import fetchData from 'utils/fetchData';
import useError from 'hooks/useError';
import useSmartlook from 'hooks/useSmartlook';
import { useNavigate } from 'react-router';
import { PSD2_RECEIVING } from 'Router/routes';

type UploadStatus = {
  status:
    | 'NEW'
    | 'INITIATED'
    | 'USER_STARTED'
    | 'USER_FINISHED'
    | 'DONE'
    | 'ERROR'
    | 'EXPIRED';
};

const useDocumentUpload = () => {
  const { showError } = useError();
  const { error } = useSmartlook();
  const navigate = useNavigate();

  const forwardUserToSDK = useCallback(async () => {
    try {
      const data = await fetchData('/client/document/upload/redirect', {
        method: 'post',
        body: JSON.stringify({
          documents: [{ type: 'ID_CARD' }],
          redirectUrl: `${window.location.origin}/first-loan/manual/psd`,
        }),
      });
      return data;
    } catch (e) {
      error('[useDocumentUpload][forwardUserToSDK]', e);
      if (e instanceof Error) {
        showError(e);
      }
      throw e;
    }
  }, [showError]);

  const sendSms = useCallback(async () => {
    try {
      await fetchData('/client/document/upload', {
        method: 'post',
        body: JSON.stringify({
          documents: [{ type: 'ID_CARD' }],
        }),
      });
    } catch (e) {
      error('[useDocumentUpload][sendSms]', e);
      if (e instanceof Error) {
        showError(e);
      }
      throw e;
    }
  }, [showError]);

  const getUploadStatus =
    useCallback(async (): Promise<UploadStatus | void> => {
      try {
        const data = await fetchData('/client/document/uploadinfo');

        return data;
      } catch (e) {
        error('[useDocumentUpload][getUploadStatus]', e);
        showError();
        throw e;
      }
    }, [showError]);

  const uploadAttachment = useCallback(
    async (formData: FormData): Promise<{ id: number }> => {
      try {
        const res = await fetchData(`/client/attachments`, {
          method: 'post',
          body: formData,
        });

        return res;
      } catch (e) {
        error('[useDocumentUpload][uploadAttachment]', e);
        showError();
        throw e;
      }
    },
    [showError],
  );

  const sendAttachmentsToOCR = useCallback(
    async (documents: { type: string; id: number }[]) => {
      try {
        const data = await fetchData('/client/document/ocr', {
          method: 'post',
          body: JSON.stringify({ documents }),
        });

        return data;
      } catch (e) {
        error('[useDocumentUpload][sendAttachmentsToOCR]', e);
        showError();
        throw e;
      }
    },
    [showError],
  );

  const sendAttachmentsToIncomeProof = useCallback(
    async (attachmentsId: number[]) => {
      // validate if data is not undefined is done in fetchData
      let data: { psdRun: boolean };
      try {
        data = await fetchData('/client/income-proof', {
          method: 'post',
          body: JSON.stringify({
            attachmentsId,
          }),
        });
      } catch (e) {
        error('[useDocumentUpload][sendAttachmentsToIncomeProof]', e);
        showError();
        throw e;
      }

      if (data.psdRun) {
        navigate(PSD2_RECEIVING);
        throw new Error('Redirecting due to PSD2 error');
      }

      return data;
    },
    [showError],
  );

  return useMemo(() => {
    return {
      sendSms,
      getUploadStatus,
      uploadAttachment,
      sendAttachmentsToOCR,
      sendAttachmentsToIncomeProof,
      forwardUserToSDK,
    };
  }, [
    sendSms,
    getUploadStatus,
    uploadAttachment,
    sendAttachmentsToOCR,
    forwardUserToSDK,
  ]);
};

export default useDocumentUpload;
